import { Box, Tooltip } from '@mui/material'
import type { Theme } from '@mui/material'
import { alpha, darken } from '@mui/material/styles'

export type LicensureStatus = 'licensed' | 'pending' | 'not_licensed' | 'not_required' | 'unknown'

// The four visual buckets the prototype renders (a red/yellow/green/grey tri-state pill).
export type LicensureBucket = 'ok' | 'pend' | 'bad' | 'na'

const LICENSURE_LABEL: Record<LicensureStatus, string> = {
  licensed: 'Licensed',
  pending: 'License pending',
  not_licensed: 'Not licensed',
  not_required: 'Not required',
  unknown: 'Unknown',
}

const licensureBucket = (status: LicensureStatus | null): LicensureBucket => {
  switch (status) {
    case 'licensed':
      return 'ok'
    case 'pending':
      return 'pend'
    case 'not_licensed':
      return 'bad'
    default:
      // not_required, unknown, or absent -> grey "not checked / not required"
      return 'na'
  }
}

/** A proposed swap is an issue (flagged, counted against the plan) when licensure is not clean. */
export const isLicensureIssue = (status: LicensureStatus | null) =>
  status === 'pending' || status === 'not_licensed' || status === 'unknown'

// Severity order (lower = more problematic), used to roll a set of statuses up to its worst.
const LICENSURE_SORT_PRIORITY: Record<LicensureStatus, number> = {
  not_licensed: 0,
  unknown: 1,
  pending: 2,
  licensed: 3,
  not_required: 4,
}

// Ascending comparator so `not_licensed`/`unknown`/`pending` rank ahead of the clean states. Used
// only to roll a member's set of statuses up to its worst (see `worstLicensure`), not to order rows.
const compareLicensure = (a: LicensureStatus | null, b: LicensureStatus | null) => {
  const priority = (status: LicensureStatus | null) =>
    status == null ? Number.MAX_SAFE_INTEGER : LICENSURE_SORT_PRIORITY[status]
  return priority(a) - priority(b)
}

/**
 * The worst (most problematic) status across a set of per-provider licensure statuses, used to
 * roll a member's current care team or proposed replacements up to a single tri-state cell.
 */
export const worstLicensure = (statuses: LicensureStatus[]): LicensureStatus | null =>
  statuses.length === 0
    ? null
    : statuses.reduce((worst, status) => (compareLicensure(status, worst) < 0 ? status : worst))

// Bucket -> colour. `na` is the neutral grey scale; ok/bad use the Firefly brand tokens
// (success green / error red) and pending falls back to the theme warning colour.
export const bucketColors = (theme: Theme, bucket: LicensureBucket) => {
  if (bucket === 'na') {
    return {
      bg: theme.palette.grey[100],
      line: theme.palette.grey[300],
      ink: theme.palette.text.secondary,
      dot: theme.palette.grey[500],
    }
  }
  // Brand tokens for ok/bad when the app theme is present; fall back to MUI's success/error so the
  // chip still renders under a default theme (e.g. tests that don't wrap in the app ThemeProvider).
  const brand = theme.palette.icons
  const base =
    bucket === 'ok'
      ? (brand?.successGreen ?? theme.palette.success.main)
      : bucket === 'bad'
        ? (brand?.errorRed ?? theme.palette.error.main)
        : theme.palette.warning.main
  // Soft pastel fill with a darkened ink for legible text on the tint.
  return {
    bg: alpha(base, 0.12),
    line: alpha(base, 0.32),
    ink: darken(base, 0.5),
    dot: base,
  }
}

export interface LicensureDetail {
  label: string
  status: LicensureStatus
}

/**
 * The tri-state licensure pill: a coloured dot plus a bold label, matching the design prototype.
 * When `details` lists the providers behind a rolled-up status, the pill gains a tooltip naming them.
 */
export const LicensureChip = ({
  status,
  details,
}: {
  status: LicensureStatus | null
  details?: LicensureDetail[]
}) => {
  if (status == null)
    return (
      <Box component="span" sx={{ color: 'text.disabled' }}>
        —
      </Box>
    )
  const bucket = licensureBucket(status)
  const chip = (
    <Box
      component="span"
      sx={theme => {
        const c = bucketColors(theme, bucket)
        return {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          fontWeight: 700,
          fontSize: 12.5,
          lineHeight: 1,
          borderRadius: 5,
          px: 1.25,
          py: 0.5,
          border: '1px solid',
          borderColor: c.line,
          bgcolor: c.bg,
          color: c.ink,
          whiteSpace: 'nowrap',
          cursor: details && details.length > 0 ? 'help' : 'default',
        }
      }}
    >
      <Box
        component="span"
        sx={theme => ({
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: bucketColors(theme, bucket).dot,
          flexShrink: 0,
        })}
      />
      {LICENSURE_LABEL[status]}
    </Box>
  )
  if (!details || details.length === 0) return chip
  return (
    <Tooltip
      arrow
      title={
        <Box sx={{ py: 0.25 }} data-testid="licensure-tooltip">
          {details.map((detail, index) => (
            <Box key={index} sx={{ fontSize: 12, whiteSpace: 'nowrap' }}>
              {detail.label} — {LICENSURE_LABEL[detail.status]}
            </Box>
          ))}
        </Box>
      }
    >
      {chip}
    </Tooltip>
  )
}
